import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import {
    ERequestStatus,
    requestByConnection,
    RequestDto,
    ResponseDto,
} from '@r1-frontend/api-domru/api-request/v1/user/request-by-connection';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { ACTIONS, CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';
import { EProductsId } from '@r1-frontend/shared/types/EProductsId';

import { IShopItem } from '~/src/api/content/shop/getShopItems';
import { ISubmitParams } from '~/src/components/RequestFormPhone';
import {
    domofonAdvatagesData,
    domofonBenefitsData,
    domofonFeaturesData,
    domofonFunctionalitiesData,
    domofonLesionsData,
    domofonQuestionsData,
} from '~/src/features/domofon/data';
import { dataLayerForCBPhonePush, IDataLayerPushUid } from '~/src/helpers/eventsForDataLayer';
import { useScroll } from '~/src/helpers/hooks/useScroll';
import { TState } from '~/src/store';
import { selectCsrf } from '~/src/store/selectors/auth/selectCsrf';

import BannerBlock from './sections/BannerBlock';
import BenefitsBlock from './sections/BenefitsBlock';
import { ConnectDomofon } from './sections/ConnectDomofon';
import FeaturesBlock from './sections/FeaturesBlock';
import FunctionalitiesBlock from './sections/FunctionalitiesBlock';
import { PresentationDomofon } from './sections/PresentationDomofon';
import ProductsBlock from './sections/ProductsBlock';
import QuestionsBlock from './sections/QuestionsBlock';
import { Statistics } from './sections/Statistics';

import * as ST from './styled';

export interface IDomofonProps {
    products: IShopItem[],
}

export const Domofon = ({ products }: IDomofonProps): JSX.Element => {
    const auth = useSelector((state: TState) => state.auth);
    const csrf = useSelector(selectCsrf);
    const [hasDomofon, setHasDomofon] = useState(true);
    const [offsetY, setOffsetY] = useState(-130); // @todo describe it and replace to a const
    const [label, setLabel] = useState('');
    const footerFormRef = useRef<HTMLDivElement>(null);
    const isMobile = useMatchMedia(`(max-width: ${wideBreakpoints.mobile})`);
    const scrollToConnectForm = useScroll({ alignment: 'center' }, offsetY);

    const submitRequest = useCallback(async(params: ISubmitParams) => {
        const dataParams: RequestDto = {
            fio: 'Потенциальный клиент домофонии',
            phone: params.phone,
            product_id: [EProductsId.domofon],
            check_call_type: window && window.location.href,
            csrf,
        };
        const resp = await doRequest.apiRequest<ResponseDto>(
            requestByConnection(dataParams),
        );
        if (resp.isSuccess) {
            const { status, message } = resp.payload;
            const ok = !!status;

            dataLayerForCBPhonePush({
                uid: auth.isAuth ? auth.clientPersonal?.agreement : null,
                action: ok ? ACTIONS.finished : ACTIONS.error,
                label: `${label} | ${message}`,
            });

            return { ok, message, status: status ? ERequestStatus.success : ERequestStatus.error };
        } else {
            dataLayerPush({
                event: EVENTS.UAevent,
                category: CATEGORIES.applicationForm,
                label,
                action: 'error',
            });

            return {
                ok: false,
                message: 'Не удалось отправить заявку.',
                status: ERequestStatus.error,
            };
        }
    }, [label]);

    const startFormFillingHandler = useCallback((from: string) => {
        setLabel(from);
        dataLayerForCBPhonePush({
            event: EVENTS.UAevent,
            category: CATEGORIES.applicationForm,
            action: ACTIONS.started,
            label: from,
            uid: auth.isAuth ? auth.clientPersonal?.agreement : null,
        } as unknown as IDataLayerPushUid);
    }, []);

    const scrollHandler = useCallback(() => {
        scrollToConnectForm(footerFormRef);
    }, [scrollToConnectForm]);

    useEffect(() => {
        if (isMobile) {
            setOffsetY(-380);
        } else {
            setOffsetY(-130);
        }
    }, [isMobile]);

    return (
        <ST.Domofon>
            <BannerBlock
                advantagesItems={domofonAdvatagesData}
                lesionsItems={domofonLesionsData}
                featuresItems={domofonFeaturesData}
                submitRequest={submitRequest}
                onStartFormFilling={() => startFormFillingHandler('banner')}
                hasDomofon={hasDomofon}
                toggleDomofon={(flag) => setHasDomofon(flag)}
            />
            <ConnectDomofon
                hasDomofon={hasDomofon}
                submitRequest={submitRequest}
                onStartFormFilling={() => startFormFillingHandler(hasDomofon ? 'intercom' : 'no intercom')}
            />
            {
                hasDomofon && <ProductsBlock products={products}/>
            }
            {
                !hasDomofon
                ? <>
                    <BenefitsBlock
                        benefitsItems={domofonBenefitsData}
                        connectBtnHandler={scrollHandler}
                    />
                    <FunctionalitiesBlock
                        functionalitiesItems={domofonFunctionalitiesData}
                        connectBtnHandler={scrollHandler}
                    />
                    <PresentationDomofon/>
                    <FeaturesBlock
                        featuresItems={domofonFeaturesData}
                        connectBtnHandler={scrollHandler}
                    />
                    <Statistics
                        key={1}
                        submitRequest={submitRequest}
                        onStartFormFilling={() => startFormFillingHandler('bottom without intercom')}
                        formRef={footerFormRef}
                    />
                </>
                : <Statistics
                    key={2}
                    submitRequest={submitRequest}
                    onStartFormFilling={() => startFormFillingHandler('bottom with intercom')}
                    formRef={footerFormRef}
                />
            }

            {!!domofonQuestionsData.length && <QuestionsBlock questions={domofonQuestionsData}/>}
        </ST.Domofon>
    );
};
